import { StrictMode, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { handleReset } from "./lib/reset";
import { ProgressContext, useLocalProgressState } from "./hooks/useLocalProgress";
import { AuthContext, useAuthState } from "./hooks/useAuth";
import "./index.css";
import App from "./App";

// ?reset clears local data before React mounts
if (new URLSearchParams(window.location.search).has("reset")) {
  handleReset(window.location.pathname);
  window.history.replaceState(null, "", window.location.pathname);
}

// Initialize Sentry (only in production with a configured DSN)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
}

function SentryFallback() {
  return (
    <div style={{ padding: 32, textAlign: "center", color: "#d7dadc" }}>
      <h2>Something went wrong</h2>
      <p>Try refreshing the page.</p>
      <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: "8px 24px", cursor: "pointer" }}>
        Refresh
      </button>
    </div>
  );
}

function SyncBridge({ syncWithServer, user }: { syncWithServer: () => Promise<void>; user: unknown }) {
  const prevUser = useRef(user);
  const syncRef = useRef(syncWithServer);
  syncRef.current = syncWithServer;

  // Sync on login (user changes from null to non-null)
  useEffect(() => {
    if (user && !prevUser.current) {
      syncRef.current();
    }
    prevUser.current = user;
  }, [user]);

  // Sync on window focus (throttled to once per 30s)
  const lastFocusSync = useRef(0);
  useEffect(() => {
    if (!user) return;
    const handleFocus = () => {
      if (Date.now() - lastFocusSync.current > 30_000) {
        lastFocusSync.current = Date.now();
        syncRef.current();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [user]);

  return null;
}

function Root() {
  const authValue = useAuthState();
  const progressValue = useLocalProgressState();
  return (
    <AuthContext.Provider value={authValue}>
      <ProgressContext.Provider value={progressValue}>
        <SyncBridge syncWithServer={progressValue.syncWithServer} user={authValue.user} />
        <Sentry.ErrorBoundary fallback={SentryFallback}>
          <App />
        </Sentry.ErrorBoundary>
      </ProgressContext.Provider>
    </AuthContext.Provider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
